import { toast } from 'react-toastify'
import { api } from '../../config/api'

const AI_HS_BASE_URL = process.env.REACT_APP_HSCOMPASS_API_BASE_URL

const chatStates = new Map()
const listeners = new Map()
const saveTimers = new Map()

const CHAT_TITLE_MAX_LENGTH = 80

const truncateText = (value, limit = CHAT_TITLE_MAX_LENGTH) => {
    const text = String(value || '').trim()
    if (!text) return ''
    if (text.length <= limit) return text
    return `${text.slice(0, limit).trimEnd()}...`
}

const getFirstUserMessage = (chat) => {
    if (!Array.isArray(chat?.messages)) return ''
    const firstUserMessage = chat.messages.find(message => message?.sender === 'user')
    return String(firstUserMessage?.message?.message || '').trim()
}

const getChatTitle = (chat) => {
    const item = chat?.itemName?.trim()
    const country = chat?.targetCountry?.trim()
    const firstUserMessage = getFirstUserMessage(chat)

    if (item && country) return truncateText(`${item} - ${country}`)
    if (item) return truncateText(item)
    if (country) return truncateText(country)
    if (firstUserMessage) return truncateText(firstUserMessage)
    return chat?.title || 'New chat'
}

const normalizeState = (chat = {}) => ({
    _id: chat._id,
    title: chat.title || 'New chat',
    itemName: chat.itemName || '',
    targetCountry: chat.targetCountry || '',
    sessionId: chat.sessionId || null,
    messages: Array.isArray(chat.messages) ? chat.messages : [],
    options: Array.isArray(chat.options) ? chat.options : [],
    finalRes: chat.finalRes ?? null,
    conversationStarted: Boolean(chat.conversationStarted),
    autoNoSent: Boolean(chat.autoNoSent),
    loading: Boolean(chat.loading),
    showProgress: Boolean(chat.showProgress),
    progress: Number(chat.progress || 0),
    waiting: chat.waiting || '',
    selectedFile: chat.selectedFile || null,
})

const toPersistableChat = (state) => ({
    _id: state._id,
    title: getChatTitle(state),
    itemName: state.itemName,
    targetCountry: state.targetCountry,
    sessionId: state.sessionId,
    messages: state.messages,
    options: state.options,
    finalRes: state.finalRes,
    conversationStarted: state.conversationStarted,
    autoNoSent: state.autoNoSent,
})

const getState = (chatId) => chatStates.get(chatId)

const emit = (chatId) => {
    const state = getState(chatId)
    if (!state) return

    const snapshot = { ...state }
    listeners.get(chatId)?.forEach(listener => listener(snapshot))
}

const schedulePersist = (chatId) => {
    const state = getState(chatId)
    if (!state?._id) return

    if (saveTimers.has(chatId)) {
        clearTimeout(saveTimers.get(chatId))
    }

    saveTimers.set(chatId, setTimeout(async () => {
        saveTimers.delete(chatId)
        const latest = getState(chatId)
        if (!latest?._id) return

        const payload = toPersistableChat(latest)
        try {
            await api.put(`/hs-compass/chats/${chatId}`, {
                title: payload.title,
                itemName: payload.itemName,
                targetCountry: payload.targetCountry,
                sessionId: payload.sessionId,
                messages: payload.messages,
                options: payload.options,
                finalRes: payload.finalRes,
                conversationStarted: payload.conversationStarted,
                autoNoSent: payload.autoNoSent,
            })
        } catch (error) {
            console.error('Failed to save HS Compass chat from runtime:', error)
        }
    }, 500))
}

const setState = (chatId, updater, persist = true) => {
    const current = getState(chatId)
    if (!current) return null

    const next = typeof updater === 'function'
        ? { ...current, ...updater(current) }
        : { ...current, ...updater }

    chatStates.set(chatId, next)
    emit(chatId)
    if (persist) schedulePersist(chatId)
    return next
}

const startProgress = (chatId) => {
    setState(chatId, {
        loading: true,
        showProgress: true,
        progress: 12,
        waiting: 'Processing request...',
    }, false)
}

const finishProgress = (chatId, patch = {}) => {
    setState(chatId, {
        ...patch,
        loading: false,
        showProgress: false,
        progress: 0,
        waiting: '',
    })
}

const ensureSession = async (chatId, authToken) => {
    const state = getState(chatId)
    if (state?.sessionId) return state.sessionId
    if (!authToken) return null

    const genId = () => {
        try {
            if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
                return window.crypto.randomUUID()
            }
        } catch (_) {
            // ignore
        }
        return `${Date.now()}-${Math.floor(Math.random() * 1e6)}`
    }

    const generated = `session-${genId()}`
    setState(chatId, { sessionId: generated })

    try {
        fetch(`${AI_HS_BASE_URL}/api/chat/message`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({ message: 'start', sessionId: generated }),
        }).catch((error) => console.error('Failed to register HS Compass session:', error))
    } catch (error) {
        console.error('Failed to register HS Compass session:', error)
    }

    return generated
}

const appendBotReply = (state, data) => ({
    messages: [...state.messages, { sender: 'bot', message: data }],
})

const handleResponse = async (chatId, data, authToken) => {
    if (!data) {
        finishProgress(chatId)
        return
    }

    if (data.sessionId) {
        setState(chatId, { sessionId: data.sessionId })
    }

    const state = getState(chatId)
    if (!state) return

    switch (data.conversationState) {
        case 'product_confirmation':
            if (state.selectedFile && !state.autoNoSent) {
                setState(chatId, { options: [], autoNoSent: true, progress: 35 })
                await sendHsCompassMessage(chatId, 'No, this is not correct', authToken, { isAuto: true })
                return
            }

            finishProgress(chatId, {
                ...appendBotReply(state, data),
                options: data.options || [],
            })
            return

        case 'awaiting_country':
        case 'initial':
        case 'datasheet_hs4_processing':
        case 'hs4_processing':
            finishProgress(chatId, {
                ...appendBotReply(state, data),
                options: data.options || [],
            })
            return

        case 'product_selection':
            finishProgress(chatId, appendBotReply(state, data))
            return

        case 'awaiting_file_upload':
            if (state.selectedFile) {
                setState(chatId, { options: [], progress: 45 })
                await uploadSelectedFile(chatId, authToken)
                return
            }

            finishProgress(chatId, {
                ...appendBotReply(state, data),
                options: ['Upload Invoice'],
            })
            return

        case 'hs4_completed':
        case 'datasheet_hs4_completed':
            await sendHsCompassMessage(chatId, 'Yes', authToken, { isAuto: true })
            return

        case 'wco_analysis_completed':
            setState(chatId, { progress: 65 }, false)
            await sendHsCompassMessage(chatId, 'Yes', authToken, { isAuto: true })
            return

        case 'classification_completed':
            finishProgress(chatId, {
                ...appendBotReply(state, data),
                options: data.options || [],
                finalRes: data?.structuredData,
            })
            return

        default:
            finishProgress(chatId)
    }
}

export const hydrateHsCompassChat = (chat = {}) => {
    if (!chat?._id) return normalizeState(chat)

    const existing = getState(chat._id)
    if (existing?.loading) return existing

    const next = existing
        ? { ...normalizeState(chat), selectedFile: existing.selectedFile }
        : normalizeState(chat)

    chatStates.set(chat._id, next)
    return next
}

export const subscribeHsCompassChat = (chatId, listener) => {
    if (!chatId) return () => {}

    if (!listeners.has(chatId)) listeners.set(chatId, new Set())
    listeners.get(chatId).add(listener)

    const state = getState(chatId)
    if (state) listener({ ...state })

    return () => {
        listeners.get(chatId)?.delete(listener)
        if (listeners.get(chatId)?.size === 0) {
            listeners.delete(chatId)
        }
    }
}

export const updateHsCompassDraft = (chatId, patch) => {
    setState(chatId, patch)
}

export const setHsCompassSelectedFile = (chatId, file) => {
    setState(chatId, { selectedFile: file }, false)
}

export const sendHsCompassMessage = async (chatId, inputValue, authToken, options = {}) => {
    const message = String(inputValue || '').trim()
    if (!chatId || !message) return
    if (!authToken) {
        toast.error('Authentication token is missing. Please log in again.')
        return
    }

    const { isAuto = false, providedSid = null } = options

    startProgress(chatId)
    setState(chatId, current => ({
        options: [],
        messages: isAuto
            ? current.messages
            : [...current.messages, { sender: 'user', message: { message } }],
    }))

    try {
        const sid = providedSid || getState(chatId)?.sessionId || await ensureSession(chatId, authToken)
        const res = await fetch(`${AI_HS_BASE_URL}/api/chat/message`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify({
                message,
                sessionId: sid || getState(chatId)?.sessionId,
            }),
        })

        if (!res.ok) throw new Error('Network response was not ok')

        const data = await res.json()
        if (data?.data?.sessionId) {
            setState(chatId, { sessionId: data.data.sessionId })
        }
        await handleResponse(chatId, data.data, authToken)
    } catch (error) {
        console.error('Error sending HS Compass message:', error)
        toast.error('Failed to send message')
        finishProgress(chatId)
    }
}

export const uploadHsCompassInvoice = async (chatId, file, authToken) => {
    if (!chatId || !file) return
    if (!authToken) {
        toast.error('Authentication token is missing. Please log in again.')
        return
    }

    setState(chatId, { selectedFile: file }, false)

    const state = getState(chatId)
    if (!state?.conversationStarted) {
        toast.success(`${file.name} is ready. Click Analyze & Classify to send.`)
        emit(chatId)
        return
    }

    await uploadSelectedFile(chatId, authToken)
}

const uploadSelectedFile = async (chatId, authToken) => {
    const state = getState(chatId)
    const file = state?.selectedFile
    if (!file || !authToken) return

    const body = new FormData()
    body.append('file', file)
    body.append('itemName', state.itemName.trim())
    body.append('targetCountry', state.targetCountry.trim())

    startProgress(chatId)
    setState(chatId, current => ({
        messages: [
            ...current.messages,
            { sender: 'user', message: { message: `Uploading Invoice ${file.name}` } },
        ],
    }))

    try {
        const sid = getState(chatId)?.sessionId || await ensureSession(chatId, authToken)
        if (!sid && !getState(chatId)?.sessionId) {
            throw new Error('No active chat session. Please start analysis first.')
        }

        body.append('sessionId', sid || getState(chatId)?.sessionId)
        const res = await fetch(`${AI_HS_BASE_URL}/api/chat/upload-datasheet`, {
            method: 'POST',
            body,
            headers: { Authorization: `Bearer ${authToken}` },
        })

        if (!res.ok) {
            let detail = 'Upload failed'
            try {
                const errJson = await res.clone().json()
                detail = errJson?.message || errJson?.error || JSON.stringify(errJson)
            } catch (_) {
                try { detail = await res.text() } catch (_) { /* ignore */ }
            }
            throw new Error(detail)
        }

        const data = await res.json()
        if (data?.data?.sessionId) {
            setState(chatId, { sessionId: data.data.sessionId })
        }
        setState(chatId, { selectedFile: null }, false)
        await handleResponse(chatId, data.data, authToken)
    } catch (error) {
        console.error('Error uploading HS Compass invoice:', error)
        toast.error(`Failed to upload invoice: ${error?.message || 'Unknown error'}`)
        finishProgress(chatId, { selectedFile: null })
    }
}

export const startHsCompassConversation = async (chatId, authToken) => {
    const state = getState(chatId)
    if (!state) return

    const itemName = state.itemName.trim()
    const targetCountry = state.targetCountry.trim()
    if (!itemName || !targetCountry) {
        toast.error('Please enter both item name and target country')
        return
    }

    setState(chatId, { conversationStarted: true })
    const sid = await ensureSession(chatId, authToken)
    const suppressUserBubble = Boolean(getState(chatId)?.selectedFile)
    await sendHsCompassMessage(chatId, `${itemName} in ${targetCountry}`, authToken, {
        isAuto: suppressUserBubble,
        providedSid: sid,
    })
}

export const getHsCompassPersistableState = (chatId) => {
    const state = getState(chatId)
    return state ? toPersistableChat(state) : null
}
